import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../components/AuthContext";

const ROLES = ["Broker", "Carrier", "Shipper", "Driver", "Consignee"];

const DOC_TYPE_LABELS = {
  BOL: { label: "Bill of Lading", icon: "📄", color: "#3b82f6" },
  POD: { label: "Proof of Delivery", icon: "✅", color: "#8b5cf6" },
  RAIL_BOL: { label: "Rail BOL", icon: "🚂", color: "#6366f1" },
  RATE_CONFIRMATION: { label: "Rate Confirmation", icon: "💰", color: "#10b981" },
};

// ============================================================================
// SIGNATURE PAD COMPONENT (Draw-to-sign)
// ============================================================================
function SignaturePad({ onSignature, width = 460, height = 180 }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1e3a8a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    setHasContent(true);
  }, [isDrawing, getPos]);

  const stopDraw = useCallback(() => {
    setIsDrawing(false);
    if (hasContent && canvasRef.current) {
      onSignature(canvasRef.current.toDataURL("image/png"));
    }
  }, [hasContent, onSignature]);

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
    onSignature(null);
  }

  return (
    <div>
      <div style={{ position: "relative", border: "2px solid #d1d5db", borderRadius: 8, background: "#fafafa", overflow: "hidden" }}>
        <canvas
          ref={canvasRef}
          width={width * 2}
          height={height * 2}
          style={{ width, height, cursor: "crosshair", display: "block", touchAction: "none" }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {!hasContent && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", color: "#d1d5db", fontSize: 16 }}>
            Sign here ✍️
          </div>
        )}
        <div style={{ position: "absolute", bottom: 30, left: 20, right: 20, borderBottom: "1px solid #d1d5db" }} />
        <div style={{ position: "absolute", bottom: 12, left: 20, fontSize: 10, color: "#9ca3af" }}>Signature</div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={clear} style={{ padding: "4px 12px", background: "#f3f4f6", border: "none", borderRadius: 4, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Clear</button>
      </div>
    </div>
  );
}

// ============================================================================
// TYPE-TO-SIGN COMPONENT
// ============================================================================
function TypeToSign({ onSignature, name }) {
  const fonts = [
    { name: "Cursive", style: "italic 36px 'Brush Script MT', 'Segoe Script', cursive" },
    { name: "Script", style: "italic 32px Georgia, 'Times New Roman', serif" },
    { name: "Formal", style: "italic bold 30px 'Palatino Linotype', 'Book Antiqua', serif" },
  ];
  const [selectedFont, setSelectedFont] = useState(0);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!name || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = fonts[selectedFont].style;
    ctx.fillStyle = "#1e3a8a";
    ctx.textBaseline = "middle";
    ctx.fillText(name, 20, canvas.height / 2);
    ctx.strokeStyle = "#d1d5db";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(10, canvas.height - 30);
    ctx.lineTo(canvas.width - 10, canvas.height - 30);
    ctx.stroke();
    onSignature(canvas.toDataURL("image/png"));
  }, [name, selectedFont]);

  return (
    <div>
      <div style={{ border: "2px solid #d1d5db", borderRadius: 8, background: "#fafafa", overflow: "hidden", marginBottom: 8 }}>
        <canvas ref={canvasRef} width={920} height={200} style={{ width: 460, height: 100, display: "block" }} />
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {fonts.map((f, i) => (
          <button key={i} onClick={() => setSelectedFont(i)} style={{
            padding: "4px 12px", background: selectedFont === i ? "#3b82f6" : "#f3f4f6",
            color: selectedFont === i ? "white" : "#6b7280",
            border: "none", borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: "pointer",
          }}>{f.name}</button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// SIGN MODAL
// ============================================================================
function SignModal({ doc, headers, onClose, onSigned }) {
  const [signMode, setSignMode] = useState("draw");
  const [signerName, setSignerName] = useState("");
  const [signerRole, setSignerRole] = useState("Broker");
  const [signerTitle, setSignerTitle] = useState("");
  const [signatureData, setSignatureData] = useState(null);
  const [signing, setSigning] = useState(false);

  async function submitSignature() {
    if (!signerName.trim()) return window.alert("Please enter your name");
    if (!signatureData) return window.alert("Please provide your signature");
    setSigning(true);
    try {
      const res = await fetch(`/api/signing/documents/${doc.id}/sign`, {
        method: "POST", headers,
        body: JSON.stringify({
          signatureType: signMode,
          signatureData: signatureData,
          typedName: signMode === "type" ? signerName : null,
          signerName, signerRole, signerTitle,
        }),
      });
      const data = await res.json();
      if (data.success) { onSigned(data); onClose(); }
      else window.alert("Signing failed: " + (data.detail || "Unknown error"));
    } catch (e) { window.alert("Error: " + e.message); }
    finally { setSigning(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "white", borderRadius: 16, width: 540, maxHeight: "90vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ background: "#1e3a8a", color: "white", padding: "16px 24px", borderRadius: "16px 16px 0 0" }}>
          <div style={{ fontWeight: 900, fontSize: 18 }}>✍️ Sign Document</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>{doc.documentNumber || doc.id?.slice(-8)} — {doc.documentType}</div>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Full Name *</label>
              <input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="John Smith" style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Role *</label>
              <select value={signerRole} onChange={e => setSignerRole(e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Title (optional)</label>
            <input value={signerTitle} onChange={e => setSignerTitle(e.target.value)} placeholder="e.g., Operations Manager" style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 16, background: "#f3f4f6", borderRadius: 8, padding: 4 }}>
            <button onClick={() => setSignMode("draw")} style={{ flex: 1, padding: "8px", background: signMode === "draw" ? "#3b82f6" : "transparent", color: signMode === "draw" ? "white" : "#6b7280", border: "none", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>✏️ Draw Signature</button>
            <button onClick={() => setSignMode("type")} style={{ flex: 1, padding: "8px", background: signMode === "type" ? "#3b82f6" : "transparent", color: signMode === "type" ? "white" : "#6b7280", border: "none", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>⌨️ Type Signature</button>
          </div>
          {signMode === "draw" ? (
            <SignaturePad onSignature={setSignatureData} />
          ) : (
            <TypeToSign onSignature={setSignatureData} name={signerName} />
          )}
          <div style={{ marginTop: 16, padding: 12, background: "#eff6ff", borderRadius: 8, fontSize: 11, color: "#1e40af", lineHeight: 1.5, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{ fontSize: 16 }}>🔗</span>
            <div><strong>Blockchain-Secured:</strong> Your signature image will be saved and its SHA-256 hash recorded on the blockchain. Any modification to the image will be detectable.</div>
          </div>
          <div style={{ marginTop: 8, padding: 12, background: "#fefce8", borderRadius: 8, fontSize: 11, color: "#854d0e", lineHeight: 1.5 }}>
            By signing, I confirm that I am the person identified above and that this electronic signature constitutes my legal signature on this document.
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <button onClick={onClose} style={{ flex: 1, padding: 12, background: "#f3f4f6", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button onClick={submitSignature} disabled={signing} style={{ flex: 1, padding: 12, background: signing ? "#9ca3af" : "#10b981", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
              {signing ? "Signing..." : "✍️ Apply Signature"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SIGNATURE CARD WITH VERIFICATION
// ============================================================================
function SignatureCard({ sig, headers }) {
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  async function verifySignature() {
    setVerifying(true);
    try {
      const res = await fetch(`/api/signing/signatures/${sig.signatureId}/verify`, { headers });
      setVerifyResult(await res.json());
    } catch (e) { setVerifyResult({ verified: false, reason: e.message }); }
    setVerifying(false);
  }

  const imageSrc = sig.signatureImageUrl || sig.signatureData;

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, background: "#fafafa" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontWeight: 700 }}>{sig.signerName}</span>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <span style={{ padding: "2px 8px", background: "#d1fae5", color: "#059669", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{sig.signerRole}</span>
          {sig.signatureType === "draw" && <span style={{ padding: "2px 6px", background: "#dbeafe", color: "#1d4ed8", borderRadius: 4, fontSize: 10, fontWeight: 600 }}>✏️ Drawn</span>}
          {sig.signatureType === "type" && <span style={{ padding: "2px 6px", background: "#fce7f3", color: "#9d174d", borderRadius: 4, fontSize: 10, fontWeight: 600 }}>⌨️ Typed</span>}
        </div>
      </div>
      {sig.signerTitle && <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>{sig.signerTitle}</div>}

      {imageSrc ? (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "white", padding: 8, marginBottom: 8 }}>
          <img src={imageSrc} alt="Signature" style={{ width: "100%", height: 60, objectFit: "contain" }} />
        </div>
      ) : sig.typedName ? (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "white", padding: 12, marginBottom: 8, fontStyle: "italic", fontSize: 24, fontFamily: "Georgia, serif", color: "#1e3a8a" }}>
          {sig.typedName}
        </div>
      ) : null}

      {sig.signatureImageHash && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 10, padding: "2px 6px", background: "#ecfdf5", color: "#065f46", borderRadius: 4, fontFamily: "monospace" }}>
            🔗 {sig.signatureImageHash.slice(0, 16)}...
          </span>
          <button onClick={verifySignature} disabled={verifying} style={{ padding: "2px 8px", background: verifying ? "#e5e7eb" : "#eff6ff", color: "#1d4ed8", border: "none", borderRadius: 4, fontSize: 10, cursor: "pointer", fontWeight: 600 }}>
            {verifying ? "..." : "🔍 Verify"}
          </button>
        </div>
      )}

      {verifyResult && (
        <div style={{
          padding: 8, borderRadius: 6, fontSize: 11, marginBottom: 6,
          background: verifyResult.verified ? "#ecfdf5" : "#fef2f2",
          color: verifyResult.verified ? "#065f46" : "#991b1b",
          border: `1px solid ${verifyResult.verified ? "#a7f3d0" : "#fecaca"}`,
        }}>
          {verifyResult.verified ? "✅ " : "⚠️ "}{verifyResult.message || verifyResult.reason}
        </div>
      )}

      <div style={{ fontSize: 11, color: "#9ca3af" }}>
        {sig.timestamp ? new Date(sig.timestamp).toLocaleString() : ""}{" | Block #"}{sig.blockNumber}
      </div>
      <div style={{ fontSize: 10, fontFamily: "monospace", color: "#d1d5db", marginTop: 4 }}>
        {sig.signatureHash?.slice(0, 32)}...
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function DocumentSigning() {
  const { adminEmail } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignModal, setShowSignModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [showSigDetail, setShowSigDetail] = useState(null);

  const headers = { "Content-Type": "application/json", "x-admin-email": adminEmail, "x-user-role": "BROKER_ADMIN" };

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [docsRes, statsRes] = await Promise.all([
        fetch("/api/signing/pending", { headers }).then(r => r.json()),
        fetch("/api/signing/stats", { headers }).then(r => r.json()),
      ]);
      setDocuments(docsRes.documents || []);
      setStats(statsRes);
    } catch (e) { console.error("Load error:", e); }
    finally { setLoading(false); }
  }

  async function loadSignatures(docId) {
    try {
      const res = await fetch(`/api/signing/documents/${docId}/signatures`, { headers });
      const data = await res.json();
      setSignatures(data.signatures || []);
      setShowSigDetail(docId);
    } catch (e) { console.error("Sig error:", e); }
  }

  function openSign(doc) { setSelectedDoc(doc); setShowSignModal(true); }
  function downloadPdf(docId) { window.open(`/api/blockchain/documents/${docId}/pdf`, "_blank"); }

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Loading document signing...</div>;

  const breakdown = stats?.signatureBreakdown || {};

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 28 }}>✍️</span> Document Signing
        </h1>
        <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: 14 }}>
          Sign documents with blockchain-verified, tamper-proof signatures
        </p>
      </div>

      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Total Documents", value: stats.totalDocuments, icon: "📄", bg: "linear-gradient(135deg, #1e3a8a, #3b82f6)" },
            { label: "Signed", value: stats.documentsWithSignatures, icon: "✍️", bg: "linear-gradient(135deg, #065f46, #10b981)" },
            { label: "Unsigned", value: stats.unsignedDocuments, icon: "⏳", bg: "linear-gradient(135deg, #92400e, #f59e0b)" },
            { label: "Total Signatures", value: stats.totalSignatures, icon: "🔏", bg: "linear-gradient(135deg, #7c3aed, #a78bfa)" },
            { label: "Fully Executed", value: stats.fullyExecuted, icon: "✅", bg: "linear-gradient(135deg, #0f766e, #14b8a6)" },
            { label: "Blockchain Verified", value: breakdown.blockchainVerified || 0, icon: "🔗", bg: "linear-gradient(135deg, #0c4a6e, #0ea5e9)" },
          ].map((s, i) => (
            <div key={i} style={{ background: s.bg, borderRadius: 12, padding: 16, color: "white" }}>
              <div style={{ fontSize: 28, fontWeight: 900 }}>{s.value}</div>
              <div style={{ opacity: 0.8, fontSize: 12 }}>{s.icon} {s.label}</div>
            </div>
          ))}
        </div>
      )}

      {(breakdown.drawn > 0 || breakdown.typed > 0) && (
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          <div style={{ padding: "8px 16px", background: "#dbeafe", borderRadius: 8, fontSize: 13 }}>✏️ <strong>{breakdown.drawn}</strong> drawn</div>
          <div style={{ padding: "8px 16px", background: "#fce7f3", borderRadius: 8, fontSize: 13 }}>⌨️ <strong>{breakdown.typed}</strong> typed</div>
          <div style={{ padding: "8px 16px", background: "#ecfdf5", borderRadius: 8, fontSize: 13 }}>🔗 <strong>{breakdown.blockchainVerified || 0}</strong> image-hashed</div>
        </div>
      )}

      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb", fontWeight: 700, fontSize: 16 }}>📄 Documents Requiring Signatures</div>
        {documents.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div><div>All documents are signed!</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Document","Load","Signatures","Signed By","Missing","Status","Actions"].map(h => (
                  <th key={h} style={{ padding: 12, textAlign: h === "Signatures" || h === "Status" || h === "Actions" ? "center" : "left", fontSize: 12, color: "#6b7280" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => {
                const typeInfo = DOC_TYPE_LABELS[doc.documentType] || { label: doc.documentType, icon: "📄", color: "#6b7280" };
                return (
                  <tr key={doc.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                    <td style={{ padding: 12 }}>
                      <span style={{ padding: "4px 8px", background: `${typeInfo.color}20`, color: typeInfo.color, borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{typeInfo.icon} {typeInfo.label}</span>
                      <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>{doc.documentNumber}</div>
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{doc.loadNumber || "N/A"}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{doc.route}</div>
                    </td>
                    <td style={{ padding: 12, textAlign: "center" }}>
                      <span style={{ fontSize: 20, fontWeight: 900, color: doc.signatureCount > 0 ? "#10b981" : "#d1d5db" }}>{doc.signatureCount}</span>
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>/3</span>
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(doc.signedRoles || []).map((role, i) => <span key={i} style={{ padding: "2px 8px", background: "#d1fae5", color: "#059669", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>✓ {role}</span>)}
                      </div>
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(doc.missingRoles || []).map((role, i) => <span key={i} style={{ padding: "2px 8px", background: "#fef3c7", color: "#92400e", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{role}</span>)}
                      </div>
                    </td>
                    <td style={{ padding: 12, textAlign: "center" }}>
                      {doc.fullyExecuted
                        ? <span style={{ padding: "4px 12px", background: "#d1fae5", color: "#059669", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>✅ Executed</span>
                        : <span style={{ padding: "4px 12px", background: "#fef3c7", color: "#92400e", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>⏳ Pending</span>}
                    </td>
                    <td style={{ padding: 12, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                        <button onClick={() => openSign(doc)} style={{ padding: "5px 10px", background: "#3b82f6", color: "white", border: "none", borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 700 }}>✍️ Sign</button>
                        <button onClick={() => loadSignatures(doc.id)} style={{ padding: "5px 10px", background: "#f3f4f6", border: "none", borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>View</button>
                        <button onClick={() => downloadPdf(doc.id)} style={{ padding: "5px 10px", background: "#fef3c7", color: "#92400e", border: "none", borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>PDF</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showSigDetail && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>🔏 Signatures for {documents.find(d => d.id === showSigDetail)?.documentNumber || showSigDetail.slice(-8)}</h3>
            <button onClick={() => setShowSigDetail(null)} style={{ padding: "4px 12px", background: "#f3f4f6", border: "none", borderRadius: 4, fontSize: 12, cursor: "pointer" }}>Close</button>
          </div>
          {signatures.length === 0 ? (
            <div style={{ color: "#9ca3af", textAlign: "center", padding: 20 }}>No signatures yet</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
              {signatures.map(sig => <SignatureCard key={sig.signatureId} sig={sig} headers={headers} />)}
            </div>
          )}
        </div>
      )}

      {showSignModal && selectedDoc && (
        <SignModal
          doc={selectedDoc}
          headers={headers}
          onClose={() => { setShowSignModal(false); setSelectedDoc(null); }}
          onSigned={() => { loadAll(); if (showSigDetail) loadSignatures(showSigDetail); }}
        />
      )}
    </div>
  );
}

export { SignModal };
